import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Row,
  Col,
  Card,
  Input,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Alert,
  Table,
  Label,
  Badge,
} from "reactstrap";
import { ImDownload } from "react-icons/im";
import { FaFilter } from "react-icons/fa";
import { Eye } from "react-feather";
import swal from "sweetalert";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import "jspdf-autotable";

/* === Shared imports (as per your rules) === */
import { CheckPermission } from "../import/CheckPermission";
import { exportDataToPDF } from "../import/Downloader";
import SuperAdminUI from "../import/SuperAdminUI";
import {
  _Get,
  _GetList,
  PurchaseOrderList,
  Stockupdate,
} from "../import/ApiEndPoint/ApiCalling";
import {
  Stock_trx_FtoW_List,
  Warehouse_ListBy_id,
} from "../import/ApiEndPoint/Api";

/* ---------- Tiny page-scoped CSS injected once ---------- */
const injectOnce = () => {
  if (document.getElementById("inward-inline-css")) return;

  const style = document.createElement("style");
  style.id = "inward-inline-css";
  style.textContent = `
    .is-toolbar .count-pill{
      background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px;
    }
    .is-table-wrap{ width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
    .is-table{ width:100%; border-collapse:collapse; table-layout:auto; min-width:900px; }
    .is-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb; padding:6px 8px; position:sticky; top:0; z-index:1; text-align:center; white-space:nowrap;
    }
    .is-table tbody td{
      padding:6px 8px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:14px; text-align:center;
    }
    .action-chips{ display:flex; justify-content:center; align-items:center; gap:8px }
    .chip{ display:inline-grid; place-items:center; width:28px; height:28px; border-radius:16px; cursor:pointer; }
    .chip-view{ background:#2563eb; }
    .dl-menu{ position:relative; }
    .dl-pop{ position:absolute; right:0; top:32px; z-index:5; border:1px solid #0b6bcb; background:#fff; border-radius:10px; overflow:hidden; min-width:130px; box-shadow:0 12px 24px rgba(0,0,0,.12); }
    .dl-item{ padding:10px 12px; cursor:pointer; font-weight:600; letter-spacing:.02em; }
    .dl-item:hover{ background:#f8fafc; }
    .is-form-card{ border:1px solid #e5e7eb; border-radius:14px; background:#fff; margin-top:10px; box-shadow:0 6px 16px rgba(0,0,0,.06); }
  `;
  document.head.appendChild(style);
};

/* ---------- Helpers ---------- */
const toStr = (v) => (v === null || v === undefined ? "" : String(v));

const fmt2 = (n) => {
  if (n === "" || n === null || n === undefined) return "";
  return Number(n || 0).toFixed(2);
};

const asDate = (s) => {
  if (!s) return "";
  return String(s).split("T")[0];
};

const safeGet = (obj, path, def = "") =>
  path
    .split(".")
    .reduce((a, k) => (a && a[k] !== undefined ? a[k] : undefined), obj) ?? def;

/* ---------- Financial Year Helpers ---------- */
const getCurrentFinancialYear = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth(); // 0 Jan, 3 Apr

  const startYear = month >= 3 ? year : year - 1;
  return `${startYear}-${startYear + 1}`;
};

const getFinancialYearFromDate = (dateValue) => {
  if (!dateValue) return "";

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = date.getMonth();

  const startYear = month >= 3 ? year : year - 1;
  return `${startYear}-${startYear + 1}`;
};

const getRowRawDate = (row) => {
  return (
    safeGet(row, "order.stockTransferDate", "") ||
    safeGet(row, "order.createdAt", "") ||
    safeGet(row, "order.orderDate", "") ||
    safeGet(row, "order.date", "") ||
    row?.stockTransferDate ||
    row?.createdAt ||
    row?.date ||
    ""
  );
};

const getRowDisplayDate = (row) => {
  return asDate(getRowRawDate(row));
};

/* ---------- Component ---------- */
export default function StockTransfer() {
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const [permissions, setPermissions] = useState({
    View: true,
    Create: true,
    Edit: true,
    Delete: true,
    Download: true,
  });

  const [masterShow, setMasterShow] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);

  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [search, setSearch] = useState("");

  const [financialYear, setFinancialYear] = useState("ALL");

  // modal for column chooser
  const [modal, setModal] = useState(false);

  // modal for transfer details
  const [viewModal, setViewModal] = useState(false);
  const [viewData, setViewData] = useState(null);

  // Column chooser state
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const addBufferRef = useRef([]);

  useEffect(() => injectOnce(), []);

  /* ---------- Define columns ---------- */
  const baseColumns = useMemo(
    () => [
      {
        key: "sno",
        header: "S.NO",
        get: (_, i) => i + 1,
      },
      {
        key: "partyName",
        header: "PARTY / FROM",
        get: (r) =>
          r?.PartyName ||
          safeGet(r, "order.warehouseFromId.warehouseName", "-"),
      },
      {
        key: "productName",
        header: "PRODUCT NAME",
        get: (r) => safeGet(r, "productId.Product_Title", "-"),
      },
      {
        key: "hsn",
        header: "HSN",
        get: (r) => safeGet(r, "productId.HSN_Code", "-"),
      },
      {
        key: "qty",
        header: "QUANTITY",
        get: (r) => r?.qty ?? r?.transferQty,
      },
      {
        key: "purchaseRate",
        header: "PURCHASE RATE",
        get: (r) => safeGet(r, "productId.Purchase_Rate", ""),
        render: (r) => fmt2(safeGet(r, "productId.Purchase_Rate", "")),
      },
      {
        key: "gstRate",
        header: "GST RATE",
        get: (r) => safeGet(r, "productId.GSTRate", ""),
      },
      {
        key: "total",
        header: "TOTAL",
        get: (r) => r?.Total,
        render: (r) => fmt2(r?.Total),
      },
      {
        key: "transferDate",
        header: "DATE",
        get: (r) => getRowDisplayDate(r),
      },
      {
        key: "financialYear",
        header: "FINANCIAL YEAR",
        get: (r) => getFinancialYearFromDate(getRowRawDate(r)) || "-",
      },
      {
        key: "status",
        header: "STATUS",
        get: (r) => safeGet(r, "order.transferStatus", "-"),
        render: (r) => (
          <Badge
            color={
              safeGet(r, "order.transferStatus", "-") === "Completed"
                ? "success"
                : "warning"
            }
          >
            {safeGet(r, "order.transferStatus", "-")}
          </Badge>
        ),
      },
    ],
    [],
  );

  const actionsColumn = useMemo(
    () => ({
      key: "_actions_",
      header: "ACTIONS",
      get: () => "",
      render: (r) => (
        <div className="action-chips">
          {permissions?.View && (
            <span
              className="chip chip-view"
              title="View"
              onClick={() => openViewModal(r)}
            >
              <Eye size={14} color="white" />
            </span>
          )}
        </div>
      ),
    }),
    [permissions],
  );

  const colMap = useMemo(() => {
    const map = new Map();
    baseColumns.forEach((c) => map.set(c.key, c));
    map.set(actionsColumn.key, actionsColumn);
    return map;
  }, [baseColumns, actionsColumn]);

  const [selectedKeys, setSelectedKeys] = useState([]);

  useEffect(() => {
    const raw = localStorage.getItem("InwardStockCols");
    let nextKeys = [];

    try {
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length) {
        nextKeys = parsed.filter((k) => colMap.has(k));
      }
    } catch {}

    if (!nextKeys.length) nextKeys = baseColumns.map((c) => c.key);

    setSelectedKeys(nextKeys);
  }, [colMap, baseColumns]);

  const visibleCols = useMemo(() => {
    const cols = selectedKeys.map((k) => colMap.get(k)).filter(Boolean);
    return [...cols, actionsColumn];
  }, [selectedKeys, colMap, actionsColumn]);

  const financialYearOptions = useMemo(() => {
    const fySet = new Set();

    rows.forEach((row) => {
      const fy = getFinancialYearFromDate(getRowRawDate(row));
      if (fy) fySet.add(fy);
    });

    fySet.add(getCurrentFinancialYear());

    return Array.from(fySet).sort((a, b) => {
      const aStart = Number(String(a).split("-")[0]);
      const bStart = Number(String(b).split("-")[0]);
      return bStart - aStart;
    });
  }, [rows]);

  useEffect(() => {
    if (
      financialYear !== "ALL" &&
      !financialYearOptions.includes(financialYear)
    ) {
      setFinancialYear("ALL");
    }
  }, [financialYear, financialYearOptions]);

  const fyFilteredRows = useMemo(() => {
    if (financialYear === "ALL") return rows;

    return rows.filter((row) => {
      const fy = getFinancialYearFromDate(getRowRawDate(row));
      return fy === financialYear;
    });
  }, [rows, financialYear]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) return fyFilteredRows;

    const searchCols = visibleCols.filter((c) => c.key !== "_actions_");

    return fyFilteredRows.filter((r, idx) =>
      searchCols.some((c) => toStr(c.get(r, idx)).toLowerCase().includes(term)),
    );
  }, [fyFilteredRows, search, visibleCols]);

  /* ---------- Data load ---------- */
  const reload = async (id, db, isWarehouseIncharge) => {
    setLoading(true);
    setErrorMsg("");

    try {
      const inside = CheckPermission("Inward Stock");

      if (inside && typeof inside === "object") {
        setPermissions((p) => ({ ...p, ...inside }));
      }

      let merged = [];

      /* Completed Purchase Orders */
      try {
        const poRes = await PurchaseOrderList(id, db);

        const completed = (poRes?.orderHistory || []).filter(
          (o) => o?.status === "completed",
        );

        const poRows = completed.flatMap((ord) =>
          (ord?.orderItems || []).map((item) => ({
            ...item,
            order: ord,
            PartyName: ord?.partyId?.CompanyName,
            qty: item?.qty,
            Purchase_Rate: item?.productId?.Purchase_Rate,
            Total: (item?.qty || 0) * (item?.productId?.Purchase_Rate || 0),
          })),
        );

        merged = merged.concat(poRows);
      } catch (err) {
        console.warn("[StockTransfer] Purchase order load skipped:", err);
      }

      /* Completed Stock Transfers */
      const trx = await _Get(Stock_trx_FtoW_List, db);

      const completedTrx = (trx?.Warehouse || []).filter(
        (t) => t?.transferStatus === "Completed",
      );

      if (isWarehouseIncharge) {
        const url = `${String(Warehouse_ListBy_id).replace(/\/+$/, "")}/${db}`;
        const wl = await _GetList(url.replace(/\/+$/, "") + `/${id}`);

        const assigned = (wl?.Warehouse || []).map((w) => w?._id);

        const filtered = completedTrx.filter((t) =>
          assigned.some((aw) => String(aw) === String(t?.warehouseToId?._id)),
        );

        const rows2 = filtered.flatMap((t) =>
          (t?.productItems || []).map((pi) => ({
            ...pi,
            order: t,
            qty: pi?.transferQty,
            PartyName: t?.warehouseFromId?.warehouseName,
            Purchase_Rate: pi?.productId?.Purchase_Rate,
            Total: (pi?.transferQty || 0) * (pi?.productId?.Purchase_Rate || 0),
          })),
        );

        merged = merged.concat(rows2);
      } else {
        const rows2 = completedTrx.flatMap((t) =>
          (t?.productItems || []).map((pi) => ({
            ...pi,
            order: t,
            qty: pi?.transferQty,
            PartyName: t?.warehouseFromId?.warehouseName,
            Purchase_Rate: pi?.productId?.Purchase_Rate,
            Total: (pi?.transferQty || 0) * (pi?.productId?.Purchase_Rate || 0),
          })),
        );

        merged = merged.concat(rows2);
      }

      setRows(merged);
      setAllRows(merged);
    } catch (e) {
      console.error("[StockTransfer] fetch error:", e);

      setErrorMsg(
        e?.response?.data?.message ||
          e?.message ||
          "Unable to load Inward Stock. Check console for details.",
      );

      setRows([]);
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      const roleName = user?.rolename?.roleName;

      setMasterShow(roleName === "MASTER");

      const isWhIncharge = roleName === "WareHouse Incharge";

      await reload(user?._id, user?.database, isWhIncharge);
    })();
  }, []);

  /* ---------- Column chooser modal logic ---------- */
  const openModal = () => {
    addBufferRef.current = [];
    setSelectedIndex(-1);
    setModal(true);
  };

  const closeModal = () => setModal(false);

  const handleCheckbox = (checked, colKey) => {
    const buf = addBufferRef.current.slice();

    if (checked) {
      if (!buf.includes(colKey)) buf.push(colKey);
    } else {
      const i = buf.indexOf(colKey);
      if (i >= 0) buf.splice(i, 1);
    }

    addBufferRef.current = buf;
  };

  const addSelectedColumns = () => {
    const next = Array.from(
      new Set([...selectedKeys, ...addBufferRef.current]),
    );
    setSelectedKeys(next);
  };

  const removeAtIndex = () => {
    if (selectedIndex < 0) return;

    const next = selectedKeys.slice();
    next.splice(selectedIndex, 1);

    setSelectedKeys(next);
    setSelectedIndex(-1);
  };

  const shiftUp = () => {
    if (selectedIndex <= 0) return;

    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];

    next.splice(selectedIndex - 1, 0, el);

    setSelectedKeys(next);
    setSelectedIndex(selectedIndex - 1);
  };

  const shiftDown = () => {
    if (selectedIndex < 0 || selectedIndex >= selectedKeys.length - 1) return;

    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];

    next.splice(selectedIndex + 1, 0, el);

    setSelectedKeys(next);
    setSelectedIndex(selectedIndex + 1);
  };

  const applyColumns = (e) => {
    e.preventDefault();
    localStorage.setItem("InwardStockCols", JSON.stringify(selectedKeys));
    closeModal();
  };

  /* ---------- Exports ---------- */
  const getExportValue = (c, r, idx) => {
    if (typeof c.render !== "function") return c.get(r, idx);

    const rendered = c.render(r, idx);

    if (
      rendered &&
      typeof rendered === "object" &&
      rendered.props &&
      rendered.props.children !== undefined
    ) {
      return rendered.props.children;
    }

    return c.get(r, idx);
  };

  const handleExportPDF = async () => {
    const cols = visibleCols.filter((c) => c.key !== "_actions_");

    const header = cols.map((c) => c.header).join(",");

    const lines = filteredRows.map((r, idx) =>
      cols
        .map((c) => {
          const val = getExportValue(c, r, idx);
          return toStr(val).replace(/"/g, '""');
        })
        .join(","),
    );

    const csv = [header, ...lines].join("\r\n");

    await exportDataToPDF(csv, "InwardStockList");
  };

  const handleExportXLSX = async (fileName) => {
    const cols = visibleCols.filter((c) => c.key !== "_actions_");

    const data = filteredRows.map((r, idx) => {
      const obj = {};

      cols.forEach((c) => {
        const val = getExportValue(c, r, idx);
        obj[c.header] = toStr(val);
      });

      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });

    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      `${fileName}.xlsx`,
    );
  };

  /* ---------- View modal ---------- */
  const openViewModal = (row) => {
    const transfer = row?.order || null;
    setViewData(transfer ? transfer : { fromPO: true, row });
    setViewModal(true);
  };

  const closeViewModal = () => {
    setViewModal(false);
    setViewData(null);
  };

  const updateStatus = async (transfer, newStatus) => {
    if (!transfer?._id) return;

    const payload = { transferStatus: newStatus };

    const ok = await swal("Warning", "Sure you want to update status?", {
      buttons: {
        cancel: "No",
        catch: {
          text: "Yes",
          value: "yes",
        },
      },
    });

    if (ok !== "yes") return;

    try {
      setLoading(true);

      await Stockupdate(transfer._id, payload);

      swal("Success", "Status Updated Successfully");

      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      const isWhIncharge = user?.rolename?.roleName === "WareHouse Incharge";

      await reload(user?._id, user?.database, isWhIncharge);

      closeViewModal();
    } catch (e) {
      console.error(e);
      swal("Update failed");
    } finally {
      setLoading(false);
    }
  };

  const downloadChallanPDF = (transfer) => {
    if (!transfer) return;

    const doc = new jsPDF("p", "pt", "a4");

    const from = transfer?.warehouseFromId?.warehouseName || "-";
    const to = transfer?.warehouseToId?.warehouseName || "-";
    const dt = transfer?.stockTransferDate
      ? asDate(transfer.stockTransferDate)
      : "-";
    const status = transfer?.transferStatus || "-";

    doc.setFontSize(14);
    doc.text("Stock Transfer Challan", 40, 40);

    doc.setFontSize(11);
    doc.text(`Transferred From: ${from}`, 40, 70);
    doc.text(`Transferred To: ${to}`, 40, 90);
    doc.text(`Date: ${dt}`, 40, 110);
    doc.text(`Status: ${status}`, 40, 130);

    const headers = [["#", "Product", "Primary Unit", "Qty"]];

    const body = (transfer?.productItems || []).map((p, i) => [
      i + 1,
      safeGet(p, "productId.Product_Title", "-"),
      p?.primaryUnit || "-",
      p?.transferQty ?? "-",
    ]);

    doc.autoTable({
      head: headers,
      body,
      startY: 150,
      styles: { fontSize: 10 },
      theme: "grid",
    });

    doc.save(`Challan_${from}_to_${to}_${dt}.pdf`);
  };

  /* ---------- MASTER helper ---------- */
  const handleDropdownChange = (val) => {
    localStorage.setItem("SuperadminIdByMaster", JSON.stringify(val));
  };

  const handleMasterSubmit = async () => {
    const saved = JSON.parse(
      localStorage.getItem("SuperadminIdByMaster") || "null",
    );

    if (!saved) return;

    const [id, db] = String(saved).split(" ");

    await reload(id, db, false);
  };

  /* ---------- Loading ---------- */
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  const noData = !filteredRows?.length;

  return (
    <>
      {errorMsg && (
        <Row>
          <Col sm="12">
            <Alert color="danger" style={{ marginBottom: 10 }}>
              {errorMsg}
            </Alert>
          </Col>
        </Row>
      )}

      {/* === Toolbar card === */}
      <Row className="is-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            style={{
              paddingTop: 4,
              paddingBottom: 4,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
              background: "#f4f7f6",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Inward Stock{" "}
                <span className="count-pill" title="records">
                  {filteredRows.length}
                </span>
              </div>

              <div style={{ flex: "1 1 160px" }} />

              {masterShow ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <SuperAdminUI
                    onDropdownChange={handleDropdownChange}
                    onSubmit={handleMasterSubmit}
                  />
                </div>
              ) : null}

              <div style={{ minWidth: 190 }}>
                <Input
                  type="select"
                  value={financialYear}
                  onChange={(e) => setFinancialYear(e.target.value)}
                  style={{ background: "#fff", fontWeight: 600 }}
                  title="Filter by Financial Year"
                >
                  <option value="ALL">All Financial Years</option>
                  {financialYearOptions.map((fy) => (
                    <option key={fy} value={fy}>
                      FY {fy}
                    </option>
                  ))}
                </Input>
              </div>

              <div
                style={{
                  minWidth: 260,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Input
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ background: "#fff" }}
                />
              </div>

              {permissions?.View && (
                <Button
                  outline
                  color="primary"
                  onClick={openModal}
                  title="Filter Columns"
                  style={{
                    height: 32,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaFilter size={14} />
                </Button>
              )}

              {permissions?.Download && (
                <div
                  className="dl-menu"
                  onMouseEnter={() => setDownloadOpen(true)}
                  onMouseLeave={() => setDownloadOpen(false)}
                >
                  <ImDownload
                    style={{ cursor: "pointer" }}
                    title="download file"
                    size={24}
                    color="#0b6bcb"
                  />

                  {downloadOpen && (
                    <div className="dl-pop">
                      <div className="dl-item" onClick={handleExportPDF}>
                        .PDF
                      </div>
                      <div
                        className="dl-item"
                        onClick={() => handleExportXLSX("InwardStockList")}
                      >
                        .XLSX
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* === Table card === */}
      <Row>
        <Col sm="12">
          <Card
            style={{
              padding: 8,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
            }}
          >
            {noData ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                No records found.
              </div>
            ) : (
              <div className="is-table-wrap">
                <table className="is-table">
                  <thead>
                    <tr>
                      {visibleCols.map((c) => (
                        <th key={c.key}>{c.header}</th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {filteredRows.map((r, idx) => (
                      <tr key={`${r?._id || r?.id || idx}`}>
                        {visibleCols.map((c) => (
                          <td key={c.key}>
                            {typeof c.render === "function"
                              ? c.render(r, idx)
                              : toStr(c.get(r, idx))}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* === Column Chooser Modal === */}
      <Modal
        isOpen={modal}
        toggle={closeModal}
        zIndex={2000}
        backdrop="static"
        keyboard={false}
        style={{ maxWidth: 1050 }}
      >
        <ModalHeader toggle={closeModal}>Change Fields</ModalHeader>

        <ModalBody>
          <Row>
            <Col lg="4" md="4" sm="12" xl="4" xs="12">
              <h5>Available Columns</h5>

              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  maxHeight: 280,
                  overflow: "auto",
                  background: "#fff",
                  padding: 8,
                }}
              >
                {baseColumns.map((col) => (
                  <div
                    key={col.key}
                    style={{
                      padding: "8px 10px",
                      borderRadius: 8,
                      border: "1px solid #e5e7eb",
                      marginBottom: 6,
                      background: "#fff",
                    }}
                  >
                    <label
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        type="checkbox"
                        onChange={(e) =>
                          handleCheckbox(e.target.checked, col.key)
                        }
                      />
                      <span>{col.header}</span>
                    </label>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 8 }}>
                <Button size="sm" color="primary" onClick={addSelectedColumns}>
                  Add Selected →
                </Button>
              </div>
            </Col>

            <Col lg="8" md="8" sm="12" xl="8" xs="12">
              <h5>Visible Columns</h5>

              <div
                style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
              >
                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 10,
                    maxHeight: 280,
                    overflow: "auto",
                    background: "#fff",
                    padding: 8,
                    flex: 1,
                  }}
                >
                  {selectedKeys.map((key, i) => {
                    const col = colMap.get(key);
                    if (!col) return null;

                    return (
                      <div
                        key={key}
                        style={{
                          padding: "8px 10px",
                          borderRadius: 8,
                          border: "1px solid #e5e7eb",
                          marginBottom: 6,
                          background: i === selectedIndex ? "#1877f2" : "#fff",
                          color: i === selectedIndex ? "#fff" : "#000",
                          cursor: "pointer",
                        }}
                        onClick={() => setSelectedIndex(i)}
                      >
                        {col.header}
                      </div>
                    );
                  })}
                </div>

                <div style={{ display: "grid", gap: 8 }}>
                  <Button size="sm" onClick={shiftUp}>
                    ↑
                  </Button>
                  <Button size="sm" onClick={shiftDown}>
                    ↓
                  </Button>
                  <Button size="sm" color="danger" onClick={removeAtIndex}>
                    Remove
                  </Button>
                </div>
              </div>

              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Button color="primary" onClick={applyColumns}>
                  Submit
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>

      {/* === View / Challan / Status Modal === */}
      <Modal
        isOpen={viewModal}
        toggle={closeViewModal}
        size="lg"
        style={{ maxWidth: 1200 }}
      >
        <ModalHeader toggle={closeViewModal}>
          {viewData?.fromPO
            ? "Purchase Order (Completed)"
            : "Stock Transfer Details"}
        </ModalHeader>

        <ModalBody>
          {!viewData ? null : viewData?.fromPO ? (
            <>
              <Row className="mb-2">
                <Col lg="3" md="4" sm="6">
                  <Label>Party:</Label>
                  <div>{viewData?.row?.PartyName || "-"}</div>
                </Col>

                <Col lg="3" md="4" sm="6">
                  <Label>Date:</Label>
                  <div>{getRowDisplayDate(viewData?.row)}</div>
                </Col>

                <Col lg="3" md="4" sm="6">
                  <Label>Financial Year:</Label>
                  <div>
                    {getFinancialYearFromDate(getRowRawDate(viewData?.row)) ||
                      "-"}
                  </div>
                </Col>

                <Col lg="3" md="4" sm="6">
                  <Label>Product:</Label>
                  <div>
                    {safeGet(viewData?.row, "productId.Product_Title", "-")}
                  </div>
                </Col>

                <Col lg="3" md="4" sm="6">
                  <Label>Qty:</Label>
                  <div>{viewData?.row?.qty}</div>
                </Col>
              </Row>
            </>
          ) : (
            <>
              <Row className="mb-2">
                <Col lg="3" md="4" sm="6">
                  <Label>Transferred From:</Label>
                  <div>
                    {safeGet(viewData, "warehouseFromId.warehouseName", "-")}
                  </div>
                </Col>

                <Col lg="3" md="4" sm="6">
                  <Label>Transferred To:</Label>
                  <div>
                    {safeGet(viewData, "warehouseToId.warehouseName", "-")}
                  </div>
                </Col>

                <Col lg="3" md="4" sm="6">
                  <Label>Date:</Label>
                  <div>{asDate(viewData?.stockTransferDate)}</div>
                </Col>

                <Col lg="3" md="4" sm="6">
                  <Label>Financial Year:</Label>
                  <div>
                    {getFinancialYearFromDate(viewData?.stockTransferDate) ||
                      "-"}
                  </div>
                </Col>

                <Col lg="3" md="4" sm="6">
                  <Label>Status:</Label>
                  <div>
                    <Badge
                      color={
                        viewData?.transferStatus === "Completed"
                          ? "success"
                          : "warning"
                      }
                    >
                      {viewData?.transferStatus}
                    </Badge>
                  </div>
                </Col>
              </Row>

              <Row className="mb-2">
                <Col className="d-flex gap-2">
                  <Button
                    color="primary"
                    onClick={() => downloadChallanPDF(viewData)}
                  >
                    Download Challan (.PDF)
                  </Button>

                  <Input
                    type="select"
                    style={{ maxWidth: 220 }}
                    value=""
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v && v !== "NA") updateStatus(viewData, v);
                    }}
                  >
                    <option value="NA">-- Update Status --</option>
                    <option value="Completed">Completed</option>
                    <option value="Pending">Pending</option>
                    <option value="Hold">Hold</option>
                  </Input>
                </Col>
              </Row>

              <Row>
                <Col>
                  <div className="d-flex justify-content-center">
                    <h5 className="m-0">Product Details</h5>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Table responsive hover className="align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>#</th>
                        <th>Product</th>
                        <th>Primary Unit</th>
                        <th>Qty</th>
                      </tr>
                    </thead>

                    <tbody>
                      {(viewData?.productItems || []).map((p, i) => (
                        <tr key={i}>
                          <td>{i + 1}</td>
                          <td>{safeGet(p, "productId.Product_Title", "-")}</td>
                          <td>{p?.primaryUnit || "-"}</td>
                          <td>{p?.transferQty ?? "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Col>
              </Row>
            </>
          )}
        </ModalBody>
      </Modal>
    </>
  );
}
